import type { HealthcareService } from '@medplum/fhirtypes';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  Button,
  Caption,
  Content,
  Grid,
  Heading,
  Icon,
  Input,
  Main,
  Stack,
  Text,
} from '@eluve/components';
import { LoadMoreRow } from '@eluve/fhir-data';
import {
  FhirResourceScope,
  RenderResource,
  buildResolutionMap,
} from '@eluve/fhir-renderer';

import { AddServiceDialog } from '../components/AddServiceDialog';
import { useServiceSearch, useServicesList } from '../data/services';

/**
 * Org-wide service catalog. Two read paths on purpose: the default list is
 * the FHIR GraphQL Connection (paged, true totals), the search box is FHIR
 * REST search — together with the REST-read detail page they demonstrate
 * the three renderer data patterns.
 */

const ServiceRows: React.FC<{ services: HealthcareService[] }> = ({
  services,
}) => {
  const map = useMemo(() => buildResolutionMap(services), [services]);
  return (
    <FhirResourceScope map={map}>
      <div data-testid="services-list">
        {services.map((service) => (
          <Link
            key={service.id}
            to={service.id ?? ''}
            data-testid="service-row"
            className="flex items-center gap-3 px-4 not-first:border-t not-first:border-border-primary hover:bg-bg-secondary"
          >
            <div className="min-w-0 flex-1">
              <RenderResource resource={service} inList />
            </div>
            <Icon
              name="ChevronRight"
              size="xs"
              className="shrink-0 text-text-tertiary"
            />
          </Link>
        ))}
      </div>
    </FhirResourceScope>
  );
};

const CenteredNote: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Grid place="center" className="px-6 py-16">
    <Text size="sm" color="tertiary" weight="regular">
      {children}
    </Text>
  </Grid>
);

export const ServicesCatalogPage: React.FC = () => {
  const listState = useServicesList();
  const services = listState.data ?? [];
  const total = listState.count ?? services.length;

  const [query, setQuery] = useState('');
  const search = useServiceSearch(query);

  const [addOpen, setAddOpen] = useState(false);

  return (
    <Main>
      <Content>
        <Stack align="stretch" className="min-h-full">
          <Stack
            direction="horizontal"
            gap={4}
            align="start"
            justify="between"
            className="border-b border-border-primary px-4 py-5 sm:px-6"
          >
            <div className="min-w-0">
              <Caption color="tertiary" className="block">
                Organization
              </Caption>
              <Stack
                direction="horizontal"
                gap={3}
                align="baseline"
                fullWidth={false}
              >
                <Heading size="2xl" level={1}>
                  Services
                </Heading>
                <Text inline size="sm" color="tertiary" weight="regular">
                  {total.toLocaleString()}{' '}
                  {total === 1 ? 'service' : 'services'}
                </Text>
              </Stack>
              <Text
                size="sm"
                color="tertiary"
                weight="regular"
                className="mt-1"
              >
                The bookable session types your organization offers.
              </Text>
            </div>
            <Button
              onClick={() => setAddOpen(true)}
              className="shrink-0"
              data-testid="new-service-button"
            >
              <Icon name="Plus" size="xs" />
              New service
            </Button>
          </Stack>

          <div className="flex-1 px-4 py-6 sm:px-6">
            <div className="mb-4 max-w-sm">
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search services…"
                aria-label="Search services"
                data-testid="service-search"
              />
            </div>

            <div className="rounded-lg border border-border-primary bg-bg-primary">
              {search.active ? (
                search.loading ? (
                  <CenteredNote>Searching…</CenteredNote>
                ) : search.resources.length === 0 ? (
                  <CenteredNote>
                    No services match “{query.trim()}”.
                  </CenteredNote>
                ) : (
                  <ServiceRows services={search.resources} />
                )
              ) : listState.failed ? (
                <CenteredNote>Couldn't load services.</CenteredNote>
              ) : listState.loading ? (
                <CenteredNote>Loading services…</CenteredNote>
              ) : services.length === 0 ? (
                <Grid place="center" className="px-6 py-16 text-center">
                  <div className="max-w-sm space-y-2">
                    <Heading size="lg" level={2}>
                      No services yet
                    </Heading>
                    <Text size="sm" color="tertiary" weight="regular">
                      Create your first bookable session type — give it a name
                      and a duration.
                    </Text>
                    <Button onClick={() => setAddOpen(true)} className="mt-2">
                      <Icon name="Plus" size="xs" />
                      New service
                    </Button>
                  </div>
                </Grid>
              ) : (
                <>
                  <ServiceRows services={services} />
                  <LoadMoreRow paging={listState} noun="services" />
                </>
              )}
            </div>
          </div>
        </Stack>

        <AddServiceDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          onCreated={listState.refetch}
        />
      </Content>
    </Main>
  );
};
